import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, EyeOff, ImageIcon } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { buildPages } from "@/components/book/layout";
import { BookPage } from "@/components/book/BookPage";
import { Lightbox } from "@/components/library/Lightbox";

const useNarrow = () => {
  const q = "(max-width: 767px)";
  const [n, setN] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setN(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return n;
};

export const yearBookSlides = (data) => (data?.chapters || []).flatMap((c) => [
  { id: `chapter-${c.year}`, media_type: "chapter", year: c.year, title: c.title, count: c.count }, ...c.items]);

const plural = (n) => `${n} ${n === 1 ? "photo" : "photos"}`;

const Cover = ({ title, data }) => (
  <div className="book-page yb-cover" data-testid="yearbook-cover-page">
    <div className={`yb-collage n${Math.min(data.highlights.length, 4)}`}>
      {data.highlights.slice(0, 4).map((h) => <img key={h.id} src={mediaUrl(h.thumb_url)} alt="" />)}
    </div>
    <h1 className="yb-cover-title" data-testid="yearbook-cover-title">{title}</h1>
    {data.span && <div className="yb-span" data-testid="yearbook-span">{data.span}</div>}
  </div>
);

export const ChapterPage = ({ year, count, title }) => (
  <div className="book-page yb-chapter" data-testid={`yearbook-chapter-page-${year}`}>
    {title ? <div className="yb-year yb-year-title" data-testid={`yearbook-chapter-title-${year}`}>{title}</div> : <div className="yb-year">{year}</div>}
    <div className="yb-count">{title ? plural(count) : `${year} · ${plural(count)}`}</div>
  </div>
);

const renderPage = (p, title, data, onOpen) => {
  if (p.type === "cover") return <Cover title={title} data={data} />;
  if (p.type === "chapter") return <ChapterPage year={p.year} count={p.count} title={p.title} />;
  return <BookPage page={p.page} onOpen={onOpen} />;
};

const BookView = ({ pages, chapterStart, title, data, onOpen }) => {
  const narrow = useNarrow();
  const per = narrow ? 1 : 2;
  const [at, setAt] = useState(0);
  const [touch, setTouch] = useState(null);
  const last = Math.max(0, Math.floor((pages.length - 1) / per) * per);
  const go = useCallback((d) => setAt((a) => Math.min(last, Math.max(0, a + d * per))), [last, per]);
  useEffect(() => setAt((a) => Math.floor(a / per) * per), [per]);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go]);
  const jump = (y) => setAt(Math.floor(chapterStart[y] / per) * per);
  const shown = pages.slice(at, at + per);
  return (
    <div className="yb-book" data-testid="yearbook-book-view"
      onTouchStart={(e) => setTouch(e.touches[0].clientX)}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
      <div className="yb-nav">
        <select className="yb-jump" value="" onChange={(e) => e.target.value && jump(e.target.value)} data-testid="yearbook-chapter-jump">
          <option value="">Jump to chapter…</option>
          {data.chapters.map((c) => <option key={c.year} value={c.year}>{c.title || `${c.year} · ${plural(c.count)}`}</option>)}
        </select>
        <span className="yb-pos" data-testid="yearbook-page-position">Page {at + 1}{per > 1 && shown.length > 1 ? `–${at + shown.length}` : ""} of {pages.length}</span>
      </div>
      <div className="yb-spread-wrap">
        <button className="yb-turn" onClick={() => go(-1)} disabled={at === 0} aria-label="Previous page" data-testid="yearbook-prev-page"><ChevronLeft size={26} /></button>
        <div key={at} className={`yb-spread per-${per}`} data-testid="yearbook-spread">
          {shown.map((p) => <div key={p.key} className="yb-slot">{renderPage(p, title, data, onOpen)}</div>)}
        </div>
        <button className="yb-turn" onClick={() => go(1)} disabled={at >= last} aria-label="Next page" data-testid="yearbook-next-page"><ChevronRight size={26} /></button>
      </div>
    </div>
  );
};

export const YearBook = ({ title, data, toolbar, head, canEdit, onExclude, onCover, onItemChange }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const flat = useMemo(() => data.chapters.flatMap((c) => c.items), [data]);
  const { pages, chapterStart } = useMemo(() => {
    const out = [{ type: "cover", key: "cover" }];
    const starts = {};
    data.chapters.forEach((c) => {
      starts[c.year] = out.length;
      out.push({ type: "chapter", key: `ch-${c.year}`, year: c.year, title: c.title, count: c.count });
      buildPages(c.items).forEach((p) => out.push({ type: "photos", key: `${c.year}-${p.key}`, page: p }));
    });
    return { pages: out, chapterStart: starts };
  }, [data]);
  const onOpen = useCallback((id) => setOpenIdx(flat.findIndex((i) => i.id === id)), [flat]);
  const actions = canEdit ? [
    { key: "yearbook-cover", label: "Use as cover", icon: <ImageIcon size={18} />, onClick: (it) => onCover(it.id) },
    { key: "yearbook-exclude", label: "Leave out of the Year Book", icon: <EyeOff size={18} />, onClick: (it) => { setOpenIdx(null); onExclude(it.id); } },
  ] : [];
  const tools = toolbar;  // one switch only: the page's Thumbnails | Book (Thumbnails = the album grid)
  return (
    <div className="yb-wrap" data-testid="yearbook">
      {head ? head(tools) : <div className="view-toolbar">{tools}</div>}
      {!data.chapters.length && <div className="book-empty" data-testid="yearbook-empty">No photos in this Year Book yet.</div>}
      {data.chapters.length > 0 && <BookView pages={pages} chapterStart={chapterStart} title={title} data={data} onOpen={onOpen} />}
      {openIdx !== null && openIdx >= 0 && <Lightbox items={flat} index={openIdx} onIndex={setOpenIdx} onClose={() => setOpenIdx(null)} actions={actions} onItemChange={onItemChange} />}
    </div>
  );
};
